import { DEFAULT_FEATURES, type FeatureId, normalizeFeatures } from "../config/features.js";

/**
 * App Registration Configuration
 *
 * Each tenant must register its own Microsoft Entra app.
 * The setup wizard can create one automatically via Azure CLI,
 * or users can create one manually and provide the client ID.
 *
 * MANUAL SETUP:
 * 1. Create app registration in Microsoft Entra portal
 * 2. Set "Supported account types" to "Accounts in any organizational directory"
 * 3. Enable "Allow public client flows" (for device code auth)
 * 4. Add only the delegated permissions for the features you intend to use
 *    (see getPermissionSummariesForFeatures below).
 * 5. Grant admin consent in your tenant
 * 6. Provide the client ID via:
 *    - Environment variable: PA_MCP_CLIENT_ID=<your-client-id>
 *    - Setup wizard: powerautomate-mcp --setup
 *    - Config file: auth.clientId in config.json
 */

/**
 * Required delegated permissions
 */
export const REQUIRED_PERMISSIONS = {
  graph: ["User.Read", "Sites.ReadWrite.All", "Files.ReadWrite.All"],
  powerAutomate: [
    "Flows.Read.All",
    "Flows.Manage.All",
    "Activity.Read.All",
    "Approvals.Manage.All",
  ],
  powerAppsService: ["User"],
  bapAdminApi: ["user_impersonation"],
  dynamicsCrm: ["user_impersonation"],
  copilotStudio: [
    "ResourceQuery.Resources.Read",
    "CopilotStudio.Copilots.Invoke",
    "CopilotStudio.MakerOperations.Read",
    "CopilotStudio.MakerOperations.ReadWrite",
    "CopilotStudio.AdminActions.Invoke",
  ],
};

/**
 * App display name used when creating via Azure CLI
 */
export const APP_DISPLAY_NAME = "Power Automate MCP";

/**
 * Native client redirect URI for public client apps
 */
export const REDIRECT_URI = "https://login.microsoftonline.com/common/oauth2/nativeclient";

export interface ResourceAccessScope {
  id: string;
  type: "Scope";
}

export interface RequiredResourceAccess {
  resourceAppId: string;
  resourceAccess: ResourceAccessScope[];
}

interface PermissionResource {
  api: string;
  resourceAppId: string;
  features: FeatureId[];
  permissions: Array<ResourceAccessScope & { name: string }>;
  usedFor: string;
  /**
   * Scope values a freshly-created SP for this resource is known to publish
   * INSTEAD of the canonical value in `permissions[].name`. Scope ids (and
   * sometimes values) differ per tenant for first-party SPs created on
   * demand; the provisioning resolver tries `name` first, then these.
   */
  alternateScopeValues?: string[];
}

/** Desired scopes for one resource, expressed by VALUE (tenant-portable). */
export interface DesiredResourceScopes {
  api: string;
  resourceAppId: string;
  /** Canonical scope values to request. */
  scopeValues: string[];
  /** Fallback values tried only when a canonical value isn't published. */
  alternateScopeValues: string[];
}

const PERMISSION_RESOURCES: PermissionResource[] = [
  {
    api: "Microsoft Graph",
    resourceAppId: "00000003-0000-0000-c000-000000000000",
    features: ["graph"],
    usedFor: "SharePoint, OneDrive, and Excel helpers",
    permissions: [
      { name: "User.Read", id: "e1fe6dd8-ba31-4d61-89e7-88639da4683d", type: "Scope" },
      { name: "Sites.ReadWrite.All", id: "89fe6a52-be36-487e-b7d8-d061c450a026", type: "Scope" },
      { name: "Files.ReadWrite.All", id: "863451e7-0667-486c-a5d6-d135439485f0", type: "Scope" },
    ],
  },
  {
    api: "Power Automate (Flow Service)",
    resourceAppId: "7df0a125-d3be-4c96-aa54-591f83ff541c",
    features: ["powerAutomate"],
    usedFor: "Core flow management, runs, approvals, and environment selection",
    permissions: [
      { name: "Flows.Read.All", id: "e45c5562-459d-4d1b-8148-83eb1b6dcf83", type: "Scope" },
      { name: "Flows.Manage.All", id: "30b2d850-00c3-4802-b7ae-ece9af9de5c6", type: "Scope" },
      { name: "Activity.Read.All", id: "822a9cde-503a-472d-a530-d1dc9cd0d52b", type: "Scope" },
      { name: "Approvals.Manage.All", id: "d05743e4-fb24-4dff-8a33-0f6c73c964bd", type: "Scope" },
    ],
  },
  {
    api: "PowerApps Service",
    resourceAppId: "475226c6-020e-4fb2-8a90-7a972cbfc1d4",
    features: ["powerApps"],
    usedFor: "Connection listing, connector metadata, custom connectors, and Power Apps maker APIs",
    permissions: [{ name: "User", id: "a4c50e78-6978-4ce3-b1c0-965c1f9b1128", type: "Scope" }],
  },
  {
    api: "BAP Admin API",
    resourceAppId: "0e0bf3cc-3078-4fd4-9ef3-cb6dc0245b10",
    features: ["dataverse", "admin", "powerPages", "powerPagesManagement"],
    usedFor: "Dataverse URL discovery, environment admin, and Power Platform admin tools",
    // A freshly-created Environment Service SP publishes "User" (tenant-local
    // id), not the canonical "user_impersonation"/4ae1bf56 pair — the AADSTS65006
    // headline case. Verified 2026-07-09 on a fresh tenant.
    alternateScopeValues: ["User"],
    permissions: [
      { name: "user_impersonation", id: "4ae1bf56-f562-4747-b7bc-2fa0874ed46f", type: "Scope" },
    ],
  },
  {
    api: "Dynamics CRM",
    resourceAppId: "00000007-0000-0000-c000-000000000000",
    features: ["dataverse", "powerPages", "powerPagesManagement"],
    usedFor:
      "Dataverse table/row CRUD, desktop flows (RPA), machines, work queues, and Power Pages configuration",
    permissions: [
      { name: "user_impersonation", id: "78ce3f0f-a1ce-49c2-8cde-64b5c0896db4", type: "Scope" },
    ],
  },
  {
    api: "Power Platform API",
    resourceAppId: "8578e004-a5c6-46e7-913e-12f58912df43",
    features: ["dataverse"],
    usedFor:
      "Copilot Studio tenant inventory, authenticated execution, maker evaluations, and administrator governance",
    permissions: [
      // Resolved from the tenant's live service principal; first-party scope
      // ids can be tenant-local even though the scope value is stable.
      { name: "ResourceQuery.Resources.Read", id: "dynamic", type: "Scope" },
      { name: "CopilotStudio.Copilots.Invoke", id: "dynamic", type: "Scope" },
      { name: "CopilotStudio.MakerOperations.Read", id: "dynamic", type: "Scope" },
      { name: "CopilotStudio.MakerOperations.ReadWrite", id: "dynamic", type: "Scope" },
      { name: "CopilotStudio.AdminActions.Invoke", id: "dynamic", type: "Scope" },
    ],
  },
  {
    api: "Power Platform API",
    resourceAppId: "8578e004-a5c6-46e7-913e-12f58912df43",
    features: ["powerApps", "powerPagesManagement"],
    usedFor:
      "Connection lifecycle (create, consent, repair, delete) and Power Pages hosting, security, custom-domain, certificate, and lifecycle management",
    permissions: [
      // These ids are resolved from the tenant's live service principal by
      // graph-provisioning.ts; first-party scope ids can be tenant-local.
      { name: "PowerPages.Websites.Read", id: "dynamic", type: "Scope" },
      { name: "PowerPages.Websites.Write", id: "dynamic", type: "Scope" },
    ],
  },
];

/**
 * Required resource access for app registration (delegated permissions).
 *
 * Microsoft Graph (00000003-0000-0000-c000-000000000000):
 *   - User.Read:            e1fe6dd8-ba31-4d61-89e7-88639da4683d
 *   - Sites.ReadWrite.All:  89fe6a52-be36-487e-b7d8-d061c450a026
 *   - Files.ReadWrite.All:  863451e7-0667-486c-a5d6-d135439485f0
 *
 * Power Automate / Flow Service (7df0a125-d3be-4c96-aa54-591f83ff541c):
 *   - Flows.Read.All:       e45c5562-459d-4d1b-8148-83eb1b6dcf83
 *   - Flows.Manage.All:     30b2d850-00c3-4802-b7ae-ece9af9de5c6
 *   - Activity.Read.All:    822a9cde-503a-472d-a530-d1dc9cd0d52b
 *   - Approvals.Manage.All: d05743e4-fb24-4dff-8a33-0f6c73c964bd
 *
 * PowerApps Service (475226c6-020e-4fb2-8a90-7a972cbfc1d4):
 *   - User:                 a4c50e78-6978-4ce3-b1c0-965c1f9b1128
 *
 * BAP Admin API (0e0bf3cc-3078-4fd4-9ef3-cb6dc0245b10):
 *   - user_impersonation:   4ae1bf56-f562-4747-b7bc-2fa0874ed46f
 *
 * Dynamics CRM (00000007-0000-0000-c000-000000000000):
 *   - user_impersonation:   78ce3f0f-a1ce-49c2-8cde-64b5c0896db4
 *
 * Copilot inventory, authenticated execution, evaluations, and governance require
 * ResourceQuery.Resources.Read, CopilotStudio.Copilots.Invoke, and
 * CopilotStudio.MakerOperations.Read/ReadWrite, and
 * CopilotStudio.AdminActions.Invoke; Power Pages
 * management additionally requires PowerPages.Websites.Read and
 * PowerPages.Websites.Write. All seven delegated scopes live on the Power Platform API (appId
 * 8578e004-a5c6-46e7-913e-12f58912df43). Setup resolves their exact ids against
 * the tenant's live service principal, then PATCHes the app registration.
 */
export function getRequiredResourceAccessForFeatures(
  features: readonly FeatureId[] = DEFAULT_FEATURES,
): RequiredResourceAccess[] {
  const enabled = new Set(normalizeFeatures(features));
  const byResource = new Map<string, Map<string, ResourceAccessScope>>();

  for (const resource of PERMISSION_RESOURCES) {
    if (!resource.features.some((feature) => enabled.has(feature))) continue;

    const scopes = byResource.get(resource.resourceAppId) ?? new Map<string, ResourceAccessScope>();
    for (const permission of resource.permissions) {
      // Tenant-local Power Platform API ids are added by the resolved PATCH.
      if (permission.id === "dynamic") continue;
      scopes.set(permission.id, { id: permission.id, type: permission.type });
    }
    if (scopes.size > 0) byResource.set(resource.resourceAppId, scopes);
  }

  return [...byResource.entries()].map(([resourceAppId, scopes]) => ({
    resourceAppId,
    resourceAccess: [...scopes.values()],
  }));
}

/**
 * Desired scopes per resource expressed by VALUE, for tenant-local resolution.
 *
 * Scope values are stable across tenants; scope ids are not for first-party
 * SPs created on demand. graph-provisioning.ts resolves these values against
 * each live SP's oauth2PermissionScopes at setup time and PATCHes the app
 * with the ids the tenant actually publishes. The hardcoded ids above remain
 * the canonical defaults used at `az ad app create` time (correct in tenants
 * whose SPs are pre-provisioned) and for the manual setup path.
 *
 * Includes dynamic-id resources such as Power Platform API so the resolver can
 * ensure their service principal exists and map exact values to tenant-local ids.
 */
export function getDesiredResourceScopesForFeatures(
  features: readonly FeatureId[] = DEFAULT_FEATURES,
): DesiredResourceScopes[] {
  const enabled = new Set(normalizeFeatures(features));
  const byResource = new Map<string, DesiredResourceScopes>();

  for (const resource of PERMISSION_RESOURCES) {
    if (!resource.features.some((feature) => enabled.has(feature))) continue;

    const entry = byResource.get(resource.resourceAppId) ?? {
      api: resource.api,
      resourceAppId: resource.resourceAppId,
      scopeValues: [],
      alternateScopeValues: [],
    };
    for (const permission of resource.permissions) {
      if (!entry.scopeValues.includes(permission.name)) {
        entry.scopeValues.push(permission.name);
      }
    }
    for (const alt of resource.alternateScopeValues ?? []) {
      if (!entry.alternateScopeValues.includes(alt)) entry.alternateScopeValues.push(alt);
    }
    byResource.set(resource.resourceAppId, entry);
  }

  return [...byResource.values()];
}

export function getPermissionSummariesForFeatures(
  features: readonly FeatureId[] = DEFAULT_FEATURES,
  _opts: { includeManual?: boolean } = {},
): string[] {
  const enabled = new Set(normalizeFeatures(features));
  return PERMISSION_RESOURCES.filter((resource) =>
    resource.features.some((feature) => enabled.has(feature)),
  ).map((resource) => {
    const permissions = resource.permissions.map((permission) => permission.name).join(", ");
    return `${resource.api}: ${permissions} — ${resource.usedFor}`;
  });
}

export const REQUIRED_RESOURCE_ACCESS = getRequiredResourceAccessForFeatures(DEFAULT_FEATURES);
